import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Droplets, 
  Lightbulb, 
  Settings, 
  Users, 
  CheckCircle2,
  Building2,
  Hotel,
  Landmark,
  Waves
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn, StaggerContainer, StaggerItem } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const serviceIcons = [Lightbulb, Settings, Droplets, Waves];

const regions = [
  { name: 'Europe', flag: '🇪🇺' },
  { name: 'Mediterranean', flag: '🌊' },
  { name: 'Middle East', flag: '🌍' },
  { name: 'Gulf Region', flag: '🌍' },
  { name: 'International', flag: '🌐' },
];

// Company founded in 2013
const FOUNDING_YEAR = 2013;
const currentYear = new Date().getFullYear();
const yearsExperience = currentYear - FOUNDING_YEAR;

export default function Home() {
  const { t } = useLanguage();

  const whyMagnumPoints = t('whyMagnum.points') || [];
  const services = t('services.items') || [];
  const processSteps = t('process.steps') || [];
  const categories = t('international.categories') || [];

  return (
    <main data-testid="home-page">
      <SEO page="home" />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-96 h-96 rounded-full bg-[#29ABE2]/5 blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-20 left-10 w-64 h-64 rounded-full bg-[#29ABE2]/5 blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-6"
            >
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4">
                {t('hero.eyebrow')}
              </span>
              <h1 className="text-5xl lg:text-7xl font-bold text-[#1A1A1A] tracking-tight uppercase leading-[1.1] mb-6">
                {t('hero.title1')}<br />
                <span className="gradient-text">{t('hero.title2')}</span><br />
                {t('hero.title3')}
              </h1>
              <p className="text-lg text-[#6C757D] leading-relaxed mb-8 max-w-lg">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-consultation"
                    className="bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider transition-colors duration-300 w-full sm:w-auto"
                  >
                    {t('hero.cta1')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-discuss"
                    variant="outline"
                    className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider transition-colors duration-300 w-full sm:w-auto"
                  >
                    {t('hero.cta2')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-6"
            >
              <div className="relative aspect-[4/3] rounded-sm overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1757548710298-b66205db281c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHx3YXRlciUyMGZlYXR1cmV8ZW58MHx8fHwxNzczODU2MzEwfDA&ixlib=rb-4.1.0&q=85"
                  alt="Premium fountain engineering"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-[#1A1A1A]/20 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-3 bg-[#29ABE2] rounded-full mt-2"
            />
          </div>
        </motion.div>
      </section>

      {/* Why Magnum Section */}
      <Section background="white">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={t('whyMagnum.eyebrow')}
              title={t('whyMagnum.title')}
              description={t('whyMagnum.description')}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {Array.isArray(whyMagnumPoints) && whyMagnumPoints.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#29ABE2] flex-shrink-0" />
                  <span className="text-sm text-[#1A1A1A]">{item}</span>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="relative aspect-square">
              <img
                src="https://images.unsplash.com/photo-1758518727707-b023e285b709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3Mzg1NjMwMHww&ixlib=rb-4.1.0&q=85"
                alt="Engineering consultation"
                className="w-full h-full object-cover rounded-sm"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#29ABE2] text-white p-6 shadow-xl">
                <p className="text-4xl font-bold">{yearsExperience}+</p>
                <p className="text-sm uppercase tracking-wider">{t('whyMagnum.yearsExperience')}</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Stats Section */}
      <Section background="surface" paddingY="py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <FadeIn delay={0}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">{yearsExperience}+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.years')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">200+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.projects')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">10+</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.countries')}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.3}>
            <div className="text-center">
              <p className="stat-number text-5xl lg:text-6xl mb-2">100%</p>
              <p className="text-sm text-[#6C757D] uppercase tracking-wider">{t('stats.satisfaction')}</p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Services Overview */}
      <Section background="white" data-testid="services-section">
        <SectionTitle
          eyebrow={t('services.eyebrow')}
          title={t('services.title')}
          description={t('services.description')}
          align="center"
        />
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.isArray(services) && services.map((service, index) => {
            const Icon = serviceIcons[index] || Lightbulb;
            return (
              <StaggerItem key={index}>
                <div className="service-card bg-white border border-gray-100 p-8 h-full">
                  <div className="w-12 h-12 bg-[#29ABE2]/10 rounded-full flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#29ABE2]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-3 uppercase tracking-wide">
                    {service.title}
                  </h3>
                  <p className="text-sm text-[#6C757D] leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
        <FadeIn delay={0.4} className="text-center mt-12">
          <Link to="/services">
            <Button
              data-testid="view-all-services-btn"
              variant="outline"
              className="border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider"
            >
              {t('services.viewAll')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </FadeIn>
      </Section>

      {/* Process Timeline */}
      <Section background="surface">
        <SectionTitle
          eyebrow={t('process.eyebrow')}
          title={t('process.title')}
          description={t('process.description')}
          align="center"
        />
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-6 min-w-max lg:min-w-0 lg:grid lg:grid-cols-5">
            {Array.isArray(processSteps) && processSteps.map((step, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <div className="relative flex flex-col items-center text-center w-48 lg:w-auto">
                  <span className="step-number mb-2">{step.number}</span>
                  <h4 className="text-lg font-bold text-[#1A1A1A] uppercase tracking-wide mb-1">
                    {step.title}
                  </h4>
                  <p className="text-sm text-[#6C757D]">{step.desc}</p>
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-[#29ABE2] to-[#E9ECEF]" />
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
        <FadeIn delay={0.5} className="text-center mt-12">
          <Link to="/process">
            <Button
              data-testid="learn-process-btn"
              className="bg-[#1A1A1A] text-white hover:bg-[#29ABE2] rounded-none px-8 py-4 text-sm font-bold uppercase tracking-wider"
            >
              {t('process.learnMore')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </FadeIn>
      </Section>

      {/* International Positioning */}
      <Section background="dark" data-testid="international-section">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={t('international.eyebrow')}
              title={t('international.title')}
              description={t('international.description')}
              dark
            />
            <div className="flex flex-wrap gap-3 mt-8">
              {regions.map((region, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-full"
                >
                  {region.flag} {region.name}
                </span>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/74b5b0bb-dd03-4c31-97c1-4a19e20989fc/images/ef197eaf31abdfbf245452508ef314ad3606010f3bbb8982a224ff01fb947e1b.png"
                alt="International fountain engineering project"
                className="w-full h-auto"
                data-testid="international-reach-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
              <div className="grid grid-cols-2 gap-4 mt-6">
                {Array.isArray(categories) && categories.map((cat, index) => {
                  const icons = [Building2, Hotel, Landmark, Users];
                  const Icon = icons[index] || Building2;
                  return (
                    <div key={index} className="bg-white/5 border border-white/10 p-5">
                      <Icon className="w-7 h-7 text-[#29ABE2] mb-3" />
                      <h4 className="text-base font-bold text-white mb-1">{cat.title}</h4>
                      <p className="text-xs text-gray-400">{cat.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Lead Capture CTA */}
      <Section background="white" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {t('cta.eyebrow')}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {t('cta.title')}
            </h2>
            <p className="text-lg text-[#6C757D] mb-8">
              {t('cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="cta-consultation-btn"
                  className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  {t('cta.button1')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="https://wa.me/34607798985" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="cta-whatsapp-btn"
                  variant="outline"
                  className="border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t('cta.button2')}
                </Button>
              </a>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
